import { toValue } from 'vue'

const MAX_WAIT_MS = 1500

const safeQuerySelector = (selector: string) => {
  try {
    return document.querySelector(selector) as HTMLElement | null
  } catch {
    return null
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  let pendingToken = 0

  const ensureAnchorScroll = () => {
    const anchorScroll = nuxtApp.$anchorScroll
    if (!anchorScroll) return

    const scrollToTarget = (target: HTMLElement) => {
      const { top, left } = target.getBoundingClientRect()
      const { behavior, offsetLeft, offsetTop } = toValue(anchorScroll.defaults.toAnchor) ?? {}
      const surfaces = toValue(anchorScroll.defaults.surfaces) ?? [document.documentElement, document.body]
      const scrollOptions = {
        behavior,
        left: left + (offsetLeft ?? 0),
        top: top + (offsetTop ?? 0),
      }

      for (const surface of surfaces) {
        surface.scrollBy(scrollOptions)
      }
    }

    const scheduleAnchorScroll = (hash: string) => {
      const token = ++pendingToken
      const startedAt = performance.now()

      const attempt = () => {
        if (token !== pendingToken) return
        const target = safeQuerySelector(hash)
        if (target) {
          scrollToTarget(target)
          return
        }
        if (performance.now() - startedAt < MAX_WAIT_MS) {
          requestAnimationFrame(attempt)
        }
      }

      requestAnimationFrame(attempt)
    }

    anchorScroll.matched.push((route) => {
      if (!route?.path?.startsWith('/listing-form/vehicle-data')) return
      if (!route.hash) return

      const target = safeQuerySelector(route.hash)
      if (target) {
        return {
          toAnchor: {
            target,
            scrollOptions: toValue(anchorScroll.defaults.toAnchor) ?? {},
            surfaces: toValue(anchorScroll.defaults.surfaces) ?? [],
          },
        }
      }

      scheduleAnchorScroll(route.hash)
      return false
    })
  }

  if (nuxtApp.$anchorScroll) {
    ensureAnchorScroll()
  } else {
    nuxtApp.hook('app:mounted', () => {
      ensureAnchorScroll()
    })
  }
})
